import { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";

import { axiosInstance } from "@/config/axios";
import { useAccessToken } from "@/hooks/auth/use-access-token";
import { useProfile } from "@/hooks/auth/use-profile";

import { ScheduleResponseDto } from "@shared/generated/api/model";

interface FetchSchedulesParams {
  variant: "instructor" | "student";
  yearMonth: Dayjs;
}

const fetchSchedules = async (
  params: FetchSchedulesParams,
  accessToken: string,
): Promise<ScheduleResponseDto[]> => {
  const endpoints = {
    instructor: "/instructor/schedule",
    student: "/user/me/schedule",
  };

  const startsAtFrom = params.yearMonth.startOf("month");
  const startsAtTo = startsAtFrom.add(1, "month");

  const { data } = await axiosInstance.get(endpoints[params.variant], {
    params: {
      startsAtFrom: startsAtFrom.format("YYYY-MM-DD"),
      startsAtTo: startsAtTo.format("YYYY-MM-DD"),
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return data.body;
};

/**
 * 특정 월의 스케줄을 조회하는 훅
 * @param variant 강사/학생 구분
 * @param yearMonth 조회할 년월 (YYYY-MM 형식)
 */
export const useFetchSchedules = (
  variant: "instructor" | "student",
  yearMonth: Dayjs,
) => {
  const { accessToken } = useAccessToken();
  const { data: profile } = useProfile();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // 클라이언트에서만 실행되도록 설정
    setIsClient(true);
  }, []);

  return useQuery({
    queryKey: [
      "schedules",
      variant,
      profile?.id,
      yearMonth.format("YYYY-MM-DD"),
    ],
    queryFn: () => fetchSchedules({ variant, yearMonth }, accessToken!),
    staleTime: 5 * 60 * 1000, // 5분
    enabled: isClient && !!accessToken && !!profile,
  });
};
